// Cambio de contraseña del usuario autenticado (Req. 19.13).
//
// Cambia la contraseña directamente contra Cognito con el `accessToken` de la
// sesión (la contraseña nunca pasa por nuestro backend). Exige la contraseña
// actual, valida que la nueva cumpla la política mínima del user pool y que la
// confirmación coincida. Tras un cambio exitoso: muestra un mensaje de éxito,
// cierra la sesión y obliga al usuario a volver a iniciar sesión.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '../auth/AuthContext.js';
import { changePassword, CognitoError } from '../auth/cognito.js';
import { AlertMessage, ButtonSpinner, Card, Icon, PageHeader } from '../ui/components.js';

/**
 * Reglas de la política de contraseñas (deben coincidir con el user pool de
 * Cognito: infra authStack.ts). Se validan en el cliente para dar feedback
 * inmediato; Cognito es la autoridad final. Las etiquetas se resuelven vía i18n.
 */
const RULES: { test: (p: string) => boolean; labelKey: string }[] = [
  { test: (p) => p.length >= 8, labelKey: 'changePassword.rules.min8' },
  { test: (p) => /[a-z]/.test(p), labelKey: 'changePassword.rules.lower' },
  { test: (p) => /[A-Z]/.test(p), labelKey: 'changePassword.rules.upper' },
  { test: (p) => /[0-9]/.test(p), labelKey: 'changePassword.rules.number' },
  { test: (p) => /[^A-Za-z0-9]/.test(p), labelKey: 'changePassword.rules.symbol' },
];

export function ChangePasswordView() {
  const { t } = useTranslation();
  const { accessToken, signOut } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const ruleStatus = useMemo(() => RULES.map((r) => ({ label: t(r.labelKey), ok: r.test(next) })), [next, t]);
  const allRulesOk = ruleStatus.every((r) => r.ok);
  const confirmOk = confirm.length > 0 && confirm === next;
  const canSubmit =
    current.length > 0 && allRulesOk && confirmOk && next !== current && !busy && accessToken !== null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !accessToken) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword(accessToken, current, next);
      // Éxito: mostramos el mensaje y, tras un momento, cerramos la sesión para
      // obligar a reiniciar con la contraseña nueva.
      setDone(true);
      setTimeout(() => {
        void signOut();
      }, 2500);
    } catch (err) {
      setError(messageFor(t, err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="flex flex-col">
        <PageHeader title={t('changePassword.pageTitle')} subtitle={t('changePassword.pageSubtitleShort')} />
        <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
          <div className="surface-card border border-goldmine-mint/40 bg-goldmine-mint/5 flex items-start gap-space-sm">
            <Icon name="check_circle" className="text-goldmine-mint text-[22px] mt-0.5" />
            <div className="flex flex-col gap-0.5">
              <span className="font-headline-sm text-headline-sm text-on-surface">{t('changePassword.doneTitle')}</span>
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                {t('changePassword.doneBody')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-space-xs text-on-surface-variant font-body-sm text-body-sm">
            <ButtonSpinner /> {t('changePassword.signingOut')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('changePassword.pageTitle')}
        subtitle={t('changePassword.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />

        <Card title={t('changePassword.cardTitle')} icon="password">
          <form onSubmit={submit} className="flex flex-col gap-space-sm">
            <label className="flex flex-col gap-1">
              <span className="section-label text-on-surface-variant">{t('changePassword.currentLabel')}</span>
              <input
                className="field-input bg-surface-deck rounded border border-border-subtle px-space-sm h-10"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="section-label text-on-surface-variant">{t('changePassword.newLabel')}</span>
              <input
                className="field-input bg-surface-deck rounded border border-border-subtle px-space-sm h-10"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
            </label>

            {/* Checklist de la política mínima (feedback en vivo). */}
            <ul className="flex flex-col gap-0.5">
              {ruleStatus.map((r) => (
                <li
                  key={r.label}
                  className={`flex items-center gap-space-xs font-body-sm text-body-sm ${
                    r.ok ? 'text-goldmine-mint' : 'text-on-surface-variant'
                  }`}
                >
                  <Icon name={r.ok ? 'check_circle' : 'radio_button_unchecked'} className="text-[16px]" />
                  {r.label}
                </li>
              ))}
              {next.length > 0 && next === current && (
                <li className="flex items-center gap-space-xs font-body-sm text-body-sm text-priority-urgent">
                  <Icon name="error" className="text-[16px]" /> {t('changePassword.mustDiffer')}
                </li>
              )}
            </ul>

            <label className="flex flex-col gap-1">
              <span className="section-label text-on-surface-variant">{t('changePassword.confirmLabel')}</span>
              <input
                className="field-input bg-surface-deck rounded border border-border-subtle px-space-sm h-10"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {confirm.length > 0 && !confirmOk && (
                <span className="font-body-sm text-body-sm text-priority-urgent">{t('changePassword.noMatch')}</span>
              )}
            </label>

            <button className="btn-primary w-full mt-space-xs" type="submit" disabled={!canSubmit}>
              {busy ? <ButtonSpinner /> : <Icon name="lock_reset" className="text-[18px]" />}
              {busy ? t('changePassword.updating') : t('changePassword.update')}
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}

/** Traduce errores de Cognito a mensajes claros (i18n). */
function messageFor(t: TFunction, err: unknown): string {
  if (err instanceof CognitoError) {
    switch (err.type) {
      case 'NotAuthorizedException':
        return t('changePassword.errWrongCurrent');
      case 'InvalidPasswordException':
        return t('changePassword.errPolicy');
      case 'LimitExceededException':
        return t('changePassword.errTooMany');
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : t('changePassword.errGeneric');
}
